import { createSelector } from "reselect"
import { get, chain, pickBy, map, find, last, includes, isEmpty } from "lodash"
import moment from "moment"
import { CATEGORIES } from "helpers/analyzeConstants"
import { getDisabled, getFactorCategoryId } from "."
import { getStartDate, getEndDate } from "./dates"
import { getFactors } from "./items"
import { getBeta } from "./beta"
import { getConcentrationData, hasReferenceEquity } from "./concentration"
import { computeCategoryItemsSubset, computeDisplayCategories, computeNonZerosKeys } from "helpers/analyze"
import { makeComputeDriftFromData } from "helpers/drift"

const EMPTY_ARRAY = []

function getFactorLabel(factors, id) {
  const factor = get(factors, id, { label: id })
  return factor.overviewLabel || factor.label
}

export function computeData(items, data) {
  return chain(data)
    .map((datum, index) => {
      const { date } = datum
      const result = chain(items)
        .map(item => {
          return ([
            item.id,
            item.fn
              ? item.fn(datum)
              : get(datum, item.field, 0),
          ])
        })
        .filter(([, value]) => !Number.isNaN(value))
        .fromPairs()
        .value()

      result.date = moment(date)
      result.index = index

      return result
    })
    .filter(datum => datum)
    .value()
}

export const getSelectedRawData = createSelector(
  [getFactorCategoryId, getBeta, getConcentrationData, hasReferenceEquity],
  (factorCategoryId, betaData, concentrationData) => factorCategoryId === "beta" ? betaData : concentrationData,
)

export const getCurrent = createSelector(
  [getSelectedRawData],
  (rawData) => last(rawData),
)

export const getAllItems = createSelector(
  [getCurrent, getDisabled, getFactors],
  (lastDatum, disabled, factors) => map(factors, (values, id) => ({
    id,
    label: getFactorLabel(factors, id),
    category: get(values, "category"),
    lastValue: get(lastDatum, ["factors", id], 0),
    field: `factors.${id}`,
    disabled: includes(disabled, id),
    area: get(values, "area"),
    percentage: get(values, "percentage"),
    color: get(values, "color"),
    tooltip: get(values, "tooltip"),
  })),
)

export const getAllCategoryItems = createSelector(
  [getAllItems, getFactorCategoryId],
  (items, factorCategoryId) => chain(items)
    .filter(item => item.category === factorCategoryId)
    .orderBy(item => Math.abs(item.lastValue), ["desc"])
    .value(),
)

export const getOrderedCategoryItems = createSelector(
  [getAllItems, getFactorCategoryId],
  (items, factorCategoryId) => chain(items)
    .filter(item => item.category === factorCategoryId)
    .orderBy(item => Math.abs(item.lastValue), ["asc", "desc"])
    .value(),
)

export const getFilteredRawData = createSelector(
  [getSelectedRawData, getStartDate, getEndDate],
  (selectedData, startDate, endDate) =>
    selectedData.filter((data) => {
      if (data.date >= startDate && data.date <= endDate) {
        return data
      } else {
        return null
      }
    }),
)

export const getData = createSelector(
  [getAllItems, getFilteredRawData],
  (items, filteredRawData) => computeData(items, filteredRawData),
)

const getNonZerosKeys = createSelector(
  [getData],
  computeNonZerosKeys,
)

export const getCategoryItemsSubset = createSelector(
  [getOrderedCategoryItems, getNonZerosKeys],
  computeCategoryItemsSubset,
)

export const getDisplayCategories = createSelector(
  [getAllItems, getNonZerosKeys],
  computeDisplayCategories,
)

export const getDrift = createSelector(
  [getBeta],
  makeComputeDriftFromData(item => item.factors),
)

export const getSelectedOption = createSelector(
  [getFactorCategoryId],
  (factorCategoryId) => find(CATEGORIES, { id: factorCategoryId }),
)

export const getSelectedFactorCategoryId = createSelector(
  [getSelectedOption],
  (selectedOption) => {
    return get(selectedOption, "id")
  },
)

export const getSelectedFactorCategoryName = createSelector(
  [getSelectedOption],
  (selectedOption) => get(selectedOption, "label"),
)

export const getFilteredData = createSelector(
  [getData, getNonZerosKeys, getCategoryItemsSubset],
  (data, keys, items) => isEmpty(items) ? EMPTY_ARRAY : map(data, item => pickBy(item, (_, key) => keys[key])),
)
